import { useEffect, useState, useCallback } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { X, LayoutDashboard, ReceiptText, ClipboardCheck, PoundSterling, Calculator, FolderOpen, MessageSquare, Send, Ruler, HardHat, Printer } from "lucide-react";
import { UnpaidBadge } from "./UnpaidBadge";
import { StagePill } from "./pipeline";
import { Drawer, Stat, money } from "./bookingdetail/shared";
import { PipelineBar } from "./bookingdetail/PipelineBar";
import { SurveyForm } from "./bookingdetail/SurveyForm";
import { Overview, SiteVisitCard } from "./bookingdetail/Overview";
import { Job } from "./bookingdetail/Job";
import { Payments } from "./bookingdetail/Payments";
import { Invoice } from "./bookingdetail/Invoice";
import { Notes } from "./bookingdetail/Notes";
import { Photos } from "./bookingdetail/Photos";
import { BuildingPlan } from "./bookingdetail/BuildingPlan";
import { Documents } from "./bookingdetail/Documents";
import { Emails } from "./bookingdetail/Emails";
import { BookingReminders } from "./bookingdetail/BookingReminders";
import { CostsTab } from "./bookingdetail/CostsTab";
import { BookPanel } from "./bookingdetail/BookPanel";
import { VisitSchedule } from "./bookingdetail/VisitSchedule";
import { LinkedQuote } from "./bookingdetail/LinkedQuote";
import { SiteLog } from "./bookingdetail/SiteLog";
import { QuoteOverview } from "./bookingdetail/QuoteOverview";
import { QuoteTab } from "./bookingdetail/QuoteTab";
import { QuoteComms } from "./QuoteComms";
import { QuoteTimeline } from "./QuoteTimeline";
import { QuoteNotes } from "./QuoteNotes";

const SUB_TABS = [
  { key: "overview", label: "Overview", Icon: LayoutDashboard },
  { key: "quote", label: "Quote", Icon: ReceiptText },
  { key: "plan", label: "Plan", Icon: Ruler, bookingOnly: true },
  { key: "survey", label: "Visit & Survey", Icon: ClipboardCheck },
  { key: "money", label: "Money", Icon: PoundSterling },
  { key: "costs", label: "Costs", Icon: Calculator },
  { key: "sitelog", label: "Site log", Icon: HardHat, bookingOnly: true },
  { key: "files", label: "Files", Icon: FolderOpen, bookingOnly: true },
  { key: "comms", label: "Comms", Icon: MessageSquare },
];
// Old deep links still resolve to the unified grouped tabs.
const LEGACY_TABS = {
  invoice: "money", payments: "money", notes: "comms", photos: "files",
  materials: "costs", documents: "files", emails: "comms",
  reminders: "overview", job: "quote", estimate: "quote", pipeline: "overview",
  visuals: "quote", timeline: "comms", dashboard: "overview",
};

function Section({ title, first, children }) {
  return (
    <div className={first ? "" : "mt-8 pt-6 border-t-2 border-[#12263A]/10"}>
      <div className="text-xs font-black uppercase tracking-widest text-[#12263A] mb-3">{title}</div>
      {children}
    </div>
  );
}

// ONE Job File from prospect to completion. kind="quote" shows the pre-booking
// view (editable quote, visit scheduling, book actions); the moment a booking
// exists the same file switches to the full booking view — same reference.
export function JobFile({ kind, id, role, onClose, initialSub }) {
  const [mode, setMode] = useState({ kind, id });
  const [data, setData] = useState(null);    // booking payload
  const [qdata, setQdata] = useState(null);  // quote payload
  const initial = LEGACY_TABS[initialSub] || initialSub;
  const [sub, setSub] = useState(SUB_TABS.some(t => t.key === initial) ? initial : "overview");
  const canManage = role === "office_staff" || role === "admin" || role === "senior_admin";

  const load = useCallback(async () => {
    try {
      if (mode.kind === "booking") {
        const r = await api.get(`/admin/booking/${mode.id}`);
        setData(r.data); setQdata(null);
      } else {
        const r = await api.get(`/admin/quote/${encodeURIComponent(mode.id)}`);
        if (r.data.booking_ref) { setMode({ kind: "booking", id: r.data.booking_ref }); return; }
        setQdata(r.data); setData(null);
      }
    } catch { toast.error("Could not load job file"); }
  }, [mode]);
  useEffect(() => { load(); }, [load]);

  const onBooked = (reference) => { setMode({ kind: "booking", id: reference }); setSub("overview"); };

  if (!data && !qdata) return (
    <Drawer onClose={onClose}><div className="p-8 text-[#6B7280]">Loading…</div></Drawer>
  );

  const isBooking = mode.kind === "booking" && !!data;
  const b = isBooking ? data.booking : null;
  const stage = isBooking ? b.stage : (qdata.stage || "new");
  const tabs = SUB_TABS.filter(t => isBooking || !t.bookingOnly);
  const feeDeferredOutstanding = isBooking && b.booking_fee_deferred && (data.paid_to_date || 0) < (b.booking_fee || 499);

  return (
    <Drawer onClose={onClose}>
      <div className="-mx-6 md:-mx-8 -mt-6 md:-mt-8 px-6 md:px-8 py-4 bg-[#12263A] text-white mb-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="label-eyebrow text-[#C79A3B]">Client job file</div>
            <h2 className="text-2xl font-black tracking-tight" data-testid="jobfile-name">{(isBooking ? b.name : qdata.name) || "—"}</h2>
            <div className="text-sm text-[#cdbfae] font-mono flex items-center gap-2 flex-wrap mt-0.5">
              <span data-testid="jobfile-reference">{(isBooking ? b.reference : qdata.reference)}{(isBooking ? b.postcode : qdata.postcode) ? ` · ${isBooking ? b.postcode : qdata.postcode}` : ""}</span>
              <StagePill stage={stage} />
              {isBooking && <UnpaidBadge booking={b} />}
              {feeDeferredOutstanding && (
                <span data-testid="deferred-fee-badge" className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full bg-[#A87E28] text-white">£499 deferred</span>
              )}
              {isBooking && b.manual_discount && <span data-testid="discount-badge" className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full bg-[#1F6B3B] text-white">{b.manual_discount.label}</span>}
              {isBooking && b.status === "refunded" && <span data-testid="refunded-badge" className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full bg-[#C2401F] text-white">Refunded</span>}
            </div>
          </div>
          <button data-testid="bookingdetail-close" onClick={onClose} className="p-2 text-white/80 hover:text-white"><X size={22} /></button>
        </div>
      </div>

      <PipelineBar kind={mode.kind} idOrRef={isBooking ? b.reference : qdata.quote_id}
        stage={stage} canManage={canManage} onChanged={() => load()} />

      {isBooking ? (
        <div className="grid grid-cols-3 gap-2 my-4">
          <Stat label="Total" value={money(b.total_inc_vat)} />
          <Stat label="Paid to date" value={money(data.paid_to_date)} />
          <Stat label="Balance" value={money(data.balance)} highlight />
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-2 my-4">
          <Stat label="Total (inc VAT)" value={money(qdata.total_inc_vat)} highlight />
          <Stat label="Route" value={qdata.route === "planning" ? "Planning" : "PD"} />
          <Stat label="Version" value={`v${qdata.version || 1}`} />
        </div>
      )}

      <div className="flex flex-wrap border-b border-[#E5E7EB] mb-4">
        {tabs.map(({ key, label, Icon }) => (
          <button key={key} data-testid={`jobfile-tab-${key}`} onClick={() => setSub(key)}
            className={`inline-flex items-center gap-1.5 px-3.5 py-2.5 text-xs font-bold uppercase tracking-widest ${sub === key ? "text-[#C79A3B] border-b-2 border-[#C79A3B]" : "text-[#6B7280] hover:text-[#12263A]"}`}>
            <Icon size={13} /> {label}
          </button>
        ))}
      </div>

      {isBooking ? (
        <BookingTabs sub={sub} data={data} b={b} role={role} canManage={canManage}
          reference={b.reference} reload={load} onClose={onClose}
          feeDeferredOutstanding={feeDeferredOutstanding} />
      ) : (
        <QuoteTabs sub={sub} qdata={qdata} setQdata={setQdata} quoteId={qdata.quote_id}
          reload={load} onClose={onClose} onBooked={onBooked} role={role} />
      )}
    </Drawer>
  );
}

function BookingTabs({ sub, data, b, role, canManage, reference, reload, onClose, feeDeferredOutstanding }) {
  return (
    <>
      {sub === "overview" && (
        <>
          <Overview b={b} canManage={canManage} reload={reload} role={role} onClose={onClose} />
          <Section title="Reminders on this job">
            <BookingReminders data={data} reference={reference} reload={reload} />
          </Section>
        </>
      )}
      {sub === "quote" && (
        <>
          <Section title="Quote & estimate (editable at any stage)" first>
            <LinkedQuote quoteId={b.quote_id} />
          </Section>
          <Section title="Scope of works">
            <Job reference={reference} canManage={canManage} reload={reload} />
          </Section>
        </>
      )}
      {sub === "plan" && (
        <>
          <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
            <p className="text-xs text-[#6B7280]">Print-ready technical build plan for use on site.</p>
            <a data-testid="plan-print-pdf" href={`${api.defaults.baseURL}/admin/booking/${reference}/building-plan.pdf`}
              target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-bold bg-[#12263A] text-white hover:bg-[#0C1B2B]">
              <Printer size={13} /> Print / PDF
            </a>
          </div>
          <BuildingPlan reference={reference} />
        </>
      )}
      {sub === "survey" && (
        <>
          <SiteVisitCard b={b} canManage={canManage} reload={reload} />
          <SurveyForm reference={reference} role={role} reload={reload} />
        </>
      )}
      {sub === "money" && (
        <>
          {b.booking_fee_deferred && (
            <div className={`border p-3 mb-4 flex items-start gap-2 ${feeDeferredOutstanding ? "border-[#F0D5B4] bg-[#FBF3EC]" : "border-[#D8EED8] bg-[#F2F9F2]"}`} data-testid="money-deferred-flag">
              <Send size={14} className={`mt-0.5 shrink-0 ${feeDeferredOutstanding ? "text-[#A87E28]" : "text-[#1F6B3B]"}`} />
              <p className="text-xs text-[#6B7280]">
                {feeDeferredOutstanding
                  ? <>The <b className="text-[#A87E28]">£499 booking fee was deferred</b>{b.booking_fee_deferred_by ? ` by ${b.booking_fee_deferred_by}` : ""} — it is included in the balance owed. Chase it with the payment-link button on the Overview tab or record a payment below.</>
                  : <>The £499 booking fee was deferred at booking and has since been covered by payments received.</>}
              </p>
            </div>
          )}
          <Section title="Invoice" first>
            <Invoice data={data} role={role} reference={reference} reload={reload} />
          </Section>
          <Section title="Payments">
            <Payments data={data} canManage={canManage} role={role} reference={reference} reload={reload} />
          </Section>
        </>
      )}
      {sub === "costs" && <CostsTab jobKey={reference} role={role} />}
      {sub === "sitelog" && <SiteLog reference={reference} clientEmail={b.email} clientName={b.name} />}
      {sub === "files" && (
        <>
          <Section title="Photos" first>
            <Photos data={data} reference={reference} reload={reload} canManage={canManage} />
          </Section>
          <Section title="Documents">
            <Documents reference={reference} />
          </Section>
        </>
      )}
      {sub === "comms" && (
        <>
          <Section title="Internal notes" first>
            <Notes data={data} reference={reference} reload={reload} />
          </Section>
          <Section title="Client emails">
            <Emails data={data} canManage={canManage} reference={reference} reload={reload} />
          </Section>
        </>
      )}
    </>
  );
}

function QuoteTabs({ sub, qdata, setQdata, quoteId, reload, onClose, onBooked, role }) {
  const handleEstimateSaved = (patch) => {
    setQdata(d => {
      if (!d) return d;
      const { estimate, revision, ...rest } = patch;
      const next = { ...d, ...rest };
      if (estimate) {
        next.estimate = estimate;
        next.total_inc_vat = estimate.total_inc_vat;
        next.estimate_revisions = [...(d.estimate_revisions || []), revision];
      }
      return next;
    });
  };
  return (
    <>
      {sub === "overview" && (
        <QuoteOverview quoteId={quoteId} data={qdata} setData={setQdata} onChanged={reload} onClose={onClose} />
      )}
      {sub === "quote" && (
        <QuoteTab quoteId={quoteId} data={qdata} onSaved={handleEstimateSaved} />
      )}
      {sub === "survey" && (
        <VisitSchedule quoteId={quoteId} data={qdata}
          onChanged={(patch) => setQdata(d => d ? { ...d, ...patch } : d)} />
      )}
      {sub === "money" && (
        <BookPanel quoteId={quoteId} data={qdata} onBooked={onBooked} />
      )}
      {sub === "costs" && <CostsTab jobKey={quoteId} role={role} />}
      {sub === "comms" && (
        <>
          <Section title="Internal notes" first>
            <QuoteNotes quoteId={quoteId} data={qdata}
              onNotesChange={(next) => setQdata(d => d ? ({ ...d, notes: next }) : d)} />
          </Section>
          <Section title="Reminders · Documents · Emails">
            <QuoteComms quoteId={quoteId} />
          </Section>
          <Section title="Timeline">
            <QuoteTimeline quoteId={quoteId} />
          </Section>
        </>
      )}
    </>
  );
}
